import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { EyebrowBullet } from "../primitives/eyebrow-bullet";

/**
 * Editorial card. Canonical card chrome (rounded-2xl, mist-dark/40
 * border, white, shadow-card with hover lift) plus a vertically-stacked
 * eyebrow + heading + body composition. Used in /settings for each
 * configurable section and anywhere the page wants a "quiet titled
 * panel with optional CTA" shape.
 *
 * The three composition slots are exposed as separate components so
 * callers can interleave non-text content (fact rows, controls, etc.)
 * between the heading and the body without losing the chrome.
 */
export interface EditorialCardProps {
  children: ReactNode;
  className?: string;
}

export function EditorialCard({ children, className }: EditorialCardProps) {
  return (
    <article
      className={cn(
        "relative mb-4 flex flex-col rounded-2xl border border-border-card bg-surface-elevated bg-gradient-card p-5 shadow-card transition-shadow duration-200 hover:shadow-card-hover",
        className,
      )}
    >
      {children}
    </article>
  );
}

export function EditorialCardEyebrow({ children }: { children: ReactNode }) {
  return <EyebrowBullet>{children}</EyebrowBullet>;
}

export function EditorialCardHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="m-0 mt-2 font-title text-[20px] font-medium leading-[1.2] tracking-[-0.015em] text-ink-primary">
      {children}
    </h3>
  );
}

export function EditorialCardBody({ children }: { children: ReactNode }) {
  return <p className="m-0 mt-2 max-w-[64ch] text-sm leading-[1.55] text-ink-body">{children}</p>;
}

/**
 * Compact label/value row used inside editorial cards. The mono caps
 * label sits left, the value (optionally mono) sits right and truncates
 * on overflow. Used for the "Profile / Plan / Persona" fact rows in
 * /settings.
 */
export interface EditorialFactRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

export function EditorialFactRow({ label, value, mono }: EditorialFactRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <dt className="shrink-0 font-sans font-medium text-xs text-ink-tertiary">{label}</dt>
      <dd className={cn("min-w-0 truncate text-right text-ink-body", mono && "font-mono text-xs")}>
        {value}
      </dd>
    </div>
  );
}
